/**
 * マイクのボタンの中身: 声で話す（ADR-0059。ADR-0055 の 5 の暫定を置き換えた）。
 * 押すと録音（MediaRecorder。iPhone の Safari は audio/mp4）→ もう一度押す（または上限の長さ）で止めて Gateway（POST /v1/stt）に送る →
 * Gateway が MacBook の中継に渡し、声の端末と同じ音声認識（whisper-server）で文にして返す → 文字の入力と同じ道（accept）で送る
 * （まとめ・割り込みの振る舞いはそのまま。ADR-0040・ADR-0042）。
 *
 * - ブラウザの聞き取り（Web Speech API）は使わない。iPhone では Apple のサーバーに声が出ることがあるため（本人の方針: 声は Jarvis の中）。
 *   Mac に届かないときも、ブラウザの聞き取りには落ちない（「Mac に届きません」と出すだけ）。
 * - 音は端末にも残さない（録った分はメモリの中だけ。送ったら捨てる）。
 * - 状態（待機・準備・録音・送信）と表示の文は、DOM と分けてこのクラスが持つ（mic.test.ts）。録音の部品・送り先は差し替えられる（原則 4）。
 */
import { GATEWAY, store } from "./api.js";

/** 録音の上限（config/client.yaml の input.mic。GET /v1/me で受け取る） */
export interface MicSettings {
  max_ms: number;
  max_bytes: number;
  min_ms: number;
}

/** config/client.yaml の初期値と同じ（未投入・古い Gateway のとき） */
export const DEFAULT_MIC: MicSettings = { max_ms: 30_000, max_bytes: 2_000_000, min_ms: 400 };

/** GET /v1/me の input から mic を読む。形が違えば既定値（値は設定の範囲に収める） */
export function micSettingsOf(input: unknown): MicSettings {
  const o = ((input as { mic?: unknown } | null)?.mic ?? {}) as Partial<
    Record<keyof MicSettings, unknown>
  >;
  const num = (x: unknown, d: number, min: number, max: number) =>
    typeof x === "number" && Number.isFinite(x) ? Math.min(Math.max(min, Math.floor(x)), max) : d;
  return {
    max_ms: num(o.max_ms, DEFAULT_MIC.max_ms, 1000, 120_000),
    max_bytes: num(o.max_bytes, DEFAULT_MIC.max_bytes, 10_000, 10_000_000),
    min_ms: num(o.min_ms, DEFAULT_MIC.min_ms, 0, 5000),
  };
}

/**
 * 録音の形式の候補（先に書いたものを選ぶ）。iPhone・Mac の Safari は audio/mp4（AAC）だけ。
 * Mac の中継は mp4 を macOS 標準の afconvert で読む。webm / ogg（Chrome・Firefox）は Mac に ffmpeg があるときだけ読める
 */
export const MIME_CANDIDATES = [
  "audio/mp4",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
] as const;

export function pickMimeType(isTypeSupported: ((t: string) => boolean) | undefined): string | null {
  if (!isTypeSupported) return null;
  for (const t of MIME_CANDIDATES) {
    try {
      if (isTypeSupported(t)) return t;
    } catch {
      /* 古いブラウザは投げることがある */
    }
  }
  return null;
}

export type MicPhase = "idle" | "starting" | "recording" | "sending";

export interface MicState {
  phase: MicPhase;
  /** 画面の下に出す短い文（空なら出さない） */
  note: string;
  /** 最後の失敗の理由（Gateway の error、ブラウザの例外の名前）。表示の色分けと記録に使う */
  code: string | null;
}

/** 送り先が返せなかった理由 */
export class MicUploadError extends Error {
  constructor(
    readonly code: string,
    readonly status: number | null = null,
    readonly trace_id: string | null = null,
  ) {
    super(code);
    this.name = "MicUploadError";
  }
}

/** 録音の部品（MediaRecorder の要るところだけ） */
export interface RecorderLike {
  readonly mimeType: string;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((e: unknown) => void) | null;
  start(timeslice?: number): void;
  stop(): void;
}

export interface MicDeps {
  /** マイクを開く（navigator.mediaDevices.getUserMedia） */
  getStream(): Promise<{ stop(): void; raw?: unknown }>;
  /** 録音の部品を作る（new MediaRecorder(stream, { mimeType })） */
  createRecorder(stream: { raw?: unknown }, mimeType: string): RecorderLike;
  /** 使える形式（MediaRecorder.isTypeSupported）。無ければマイクは使えない */
  mimeType: string | null;
  /** 録った音を文にする（POST /v1/stt） */
  upload(audio: Blob, durationMs: number): Promise<{ text: string; trace_id: string }>;
  now(): number;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(h: unknown): void;
}

/** Mac（中継・音声認識）に届かないと見なす理由。表示は「Mac に届きません」だけ（詳しくは span と runbook） */
const UNREACHABLE = new Set([
  "relay_unreachable",
  "relay_rejected",
  "relay_bad_response",
  "stt_unavailable",
  "speech_not_configured",
]);

export const MIC_UNREACHABLE = "Mac に届きません";
export const MIC_UNAVAILABLE = "この端末では声の入力が使えません。文字で話しかけてください。";
export const MIC_LISTENING = "聞いています…（もう一度押すと送ります）";
export const MIC_SENDING = "文にしています…";

/** 失敗の理由を、画面に出す短い文にする */
export function micErrorText(code: string): string {
  if (UNREACHABLE.has(code)) return MIC_UNREACHABLE;
  const map: Record<string, string> = {
    NotAllowedError: "マイクの使用が許可されていません。端末の設定で許可してください。",
    SecurityError: "マイクの使用が許可されていません。端末の設定で許可してください。",
    NotFoundError: "マイクが見つかりません。",
    NotReadableError: "マイクを開けません（ほかのアプリが使っているかもしれません）。",
    InvalidStateError:
      "録音を始められませんでした。アプリを閉じて開き直してから、もう一度押してください。",
    network: "通信できません。",
    unauthorized: "ログインし直してください。",
    passkey_session_required: "この端末では使えません（パスキーでログインした端末だけ）。",
    audio_too_long: "長すぎました。短く区切って話してください。",
    audio_too_large: "長すぎました。短く区切って話してください。",
    unsupported_audio: "この端末の録音の形式を Mac が読めません。",
    too_short: "短すぎました。押してから話して、終わったらもう一度押してください。",
    no_speech: "聞き取れませんでした。もう一度お願いします。",
    unavailable: MIC_UNAVAILABLE,
  };
  return map[code] ?? `聞き取れませんでした（${code}）`;
}

/**
 * 録音して Mac で文にする。押すたびに toggle()。状態が変わるたびに onState、文ができたら onText（空の文は渡さない）。
 * 一度に 1 つだけ（送っている間に押しても何もしない）
 */
export class RecorderInput {
  private s: MicState = { phase: "idle", note: "", code: null };
  private stream: { stop(): void } | null = null;
  private rec: RecorderLike | null = null;
  private chunks: Blob[] = [];
  private bytes = 0;
  private startedAt = 0;
  private timer: unknown = null;
  private cancelStart = false;
  private overflow = false;
  settings: MicSettings;

  constructor(
    private readonly deps: MicDeps,
    private readonly handlers: {
      onText: (text: string) => void;
      onState: (s: MicState) => void;
    },
    settings: MicSettings = DEFAULT_MIC,
  ) {
    this.settings = settings;
  }

  get available(): boolean {
    return this.deps.mimeType !== null;
  }
  get state(): MicState {
    return this.s;
  }
  /** 録音中か（ボタンの見た目） */
  get listening(): boolean {
    return this.s.phase === "recording" || this.s.phase === "starting";
  }

  private set(phase: MicPhase, note: string, code: string | null = null) {
    this.s = { phase, note, code };
    this.handlers.onState(this.s);
  }

  toggle(): void {
    if (!this.available) {
      this.set("idle", MIC_UNAVAILABLE, "unavailable");
      return;
    }
    switch (this.s.phase) {
      case "idle":
        void this.start();
        return;
      case "starting":
        // マイクを開く途中で押した: 開いたらすぐ閉じる
        this.cancelStart = true;
        return;
      case "recording":
        this.stop();
        return;
      case "sending":
        return;
    }
  }

  /** 録音を止める（送る）。録音中でなければ何もしない */
  stop(): void {
    if (this.s.phase !== "recording" || !this.rec) return;
    this.clear();
    try {
      this.rec.stop();
    } catch {
      this.finishRecording();
    }
  }

  /** 録音を捨てて止める（画面を離れたとき・ログアウト）。送らない */
  cancel(): void {
    if (this.s.phase === "starting") this.cancelStart = true;
    if (this.s.phase !== "recording") return;
    this.clear();
    const rec = this.rec;
    this.rec = null;
    if (rec) {
      rec.onstop = null;
      rec.ondataavailable = null;
      try {
        rec.stop();
      } catch {
        /* 止まっている */
      }
    }
    this.release();
    this.set("idle", "");
  }

  private clear() {
    if (this.timer !== null) this.deps.clearTimer(this.timer);
    this.timer = null;
  }

  private release() {
    try {
      this.stream?.stop();
    } catch {
      /* 閉じている */
    }
    this.stream = null;
    this.chunks = [];
    this.bytes = 0;
  }

  private async start() {
    this.cancelStart = false;
    this.overflow = false;
    this.set("starting", "マイクを準備しています…");
    let stream: { stop(): void; raw?: unknown };
    try {
      stream = await this.deps.getStream();
    } catch (e) {
      const code = (e as { name?: string } | null)?.name || "unavailable";
      return this.set("idle", micErrorText(code), code);
    }
    if (this.cancelStart) {
      stream.stop();
      return this.set("idle", "");
    }
    this.stream = stream;
    const mime = this.deps.mimeType as string;
    let rec: RecorderLike;
    try {
      rec = this.deps.createRecorder(stream, mime);
    } catch (e) {
      this.release();
      const code = (e as { name?: string } | null)?.name || "unavailable";
      return this.set("idle", micErrorText(code), code);
    }
    this.rec = rec;
    this.chunks = [];
    this.bytes = 0;
    this.wire(rec);
    // 1 秒ごとに切って受け取る（大きさを途中で確かめる）。iPhone の Safari は start(timeslice) や形式の指定を
    // InvalidStateError で断ることがある（2026-10-11 本人の iPhone で）。断られたら、切らずに始める → 形式を指定せずに作り直す、の順に試す
    const attempts: Array<() => RecorderLike> = [
      () => (rec.start(1000), rec),
      () => (rec.start(), rec),
      () => {
        const again = this.deps.createRecorder(stream, "");
        this.wire(again);
        again.start();
        return again;
      },
    ];
    let lastErr: unknown = null;
    let started: RecorderLike | null = null;
    for (const attempt of attempts) {
      try {
        started = attempt();
        break;
      } catch (e) {
        lastErr = e;
        if ((e as { name?: string } | null)?.name !== "InvalidStateError") break;
      }
    }
    if (!started) {
      this.rec = null;
      this.release();
      const code = (lastErr as { name?: string } | null)?.name || "unavailable";
      return this.set("idle", micErrorText(code), code);
    }
    this.rec = started;
    this.startedAt = this.deps.now();
    this.timer = this.deps.setTimer(() => this.stop(), this.settings.max_ms);
    this.set("recording", MIC_LISTENING);
  }

  /** 録音の部品に受け取りの口をつなぐ（作り直したときも同じ口） */
  private wire(rec: RecorderLike) {
    rec.ondataavailable = (ev) => {
      if (!ev.data || ev.data.size === 0) return;
      this.chunks.push(ev.data);
      this.bytes += ev.data.size;
      // 大きすぎる録音は送らない（途中で切った音は読めない形式があるので、止めて捨てる）
      if (this.bytes > this.settings.max_bytes && !this.overflow) {
        this.overflow = true;
        this.stop();
      }
    };
    rec.onstop = () => this.finishRecording();
    rec.onerror = () => {
      this.clear();
      this.rec = null;
      this.release();
      this.set("idle", micErrorText("NotReadableError"), "recorder_error");
    };
  }

  private finishRecording() {
    const rec = this.rec;
    this.rec = null;
    this.clear();
    const durationMs = Math.max(0, Math.round(this.deps.now() - this.startedAt));
    const chunks = this.chunks;
    const type = rec?.mimeType || (this.deps.mimeType as string);
    const overflow = this.overflow;
    this.release();
    if (overflow) return this.set("idle", micErrorText("audio_too_large"), "audio_too_large");
    if (durationMs < this.settings.min_ms || chunks.length === 0)
      return this.set("idle", micErrorText("too_short"), "too_short");
    const blob = new Blob(chunks, { type });
    this.set("sending", MIC_SENDING);
    void this.deps
      .upload(blob, durationMs)
      .then((r) => {
        const text = r.text.trim();
        if (!text) return this.set("idle", micErrorText("no_speech"), "no_speech");
        this.set("idle", "");
        this.handlers.onText(text);
      })
      .catch((e: unknown) => {
        const code = e instanceof MicUploadError ? e.code : "network";
        this.set("idle", micErrorText(code), code);
      });
  }
}

/** POST /v1/stt に録った音を送る（パスキーのセッションの Bearer）。本文は音そのもの */
export function gatewayUpload(
  fetchImpl: typeof fetch = (...a) => fetch(...a),
  base = GATEWAY,
  token: () => string | null = () => store.token,
  timeoutMs = 40_000,
): MicDeps["upload"] {
  return async (audio, durationMs) => {
    const res = await fetchImpl(`${base}/v1/stt`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token() ?? ""}`,
        "Content-Type": audio.type || "application/octet-stream",
        "X-Audio-Duration-Ms": String(Math.round(durationMs)),
      },
      body: audio,
      signal: AbortSignal.timeout(timeoutMs),
    }).catch(() => {
      throw new MicUploadError("network");
    });
    const j = (await res.json().catch(() => ({}))) as {
      error?: string;
      text?: unknown;
      trace_id?: string;
    };
    if (!res.ok)
      throw new MicUploadError(
        res.status === 401 ? "unauthorized" : (j.error ?? `http_${res.status}`),
        res.status,
        j.trace_id ?? null,
      );
    if (typeof j.text !== "string") throw new MicUploadError("relay_bad_response", res.status);
    return { text: j.text, trace_id: j.trace_id ?? "" };
  };
}

/** ブラウザの部品で組む（MediaRecorder・getUserMedia が無い端末では available が false） */
export function browserMicDeps(upload: MicDeps["upload"] = gatewayUpload()): MicDeps {
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  const md = (globalThis as { navigator?: Navigator }).navigator?.mediaDevices;
  const mimeType =
    MR && md?.getUserMedia ? pickMimeType((t) => MR.isTypeSupported?.(t) ?? false) : null;
  return {
    mimeType,
    async getStream() {
      if (!md?.getUserMedia) throw Object.assign(new Error("no mic"), { name: "unavailable" });
      const s = await md.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
      return {
        raw: s,
        stop: () => {
          for (const t of s.getTracks()) t.stop();
        },
      };
    },
    createRecorder(stream, mime) {
      if (!MR) throw Object.assign(new Error("no recorder"), { name: "unavailable" });
      // 形式が空なら指定しない（端末の既定。iPhone で指定を断られたときの作り直し）
      return (mime
        ? new MR(stream.raw as MediaStream, { mimeType: mime })
        : new MR(stream.raw as MediaStream)) as unknown as RecorderLike;
    },
    upload,
    now: () => performance.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  };
}
